import React, { useState, useEffect } from "react";
import { DS } from "../theme";

const BG_PIECES = [
  { p:"♔", top:"8%",  left:"4%",  rot:-15, size:180 },
  { p:"♕", top:"6%",  left:"72%", rot:10,  size:160 },
  { p:"♖", top:"62%", left:"8%",  rot:20,  size:140 },
  { p:"♗", top:"14%", left:"86%", rot:-8,  size:130 },
  { p:"♘", top:"70%", left:"78%", rot:5,   size:150 },
  { p:"♙", top:"50%", left:"46%", rot:-20, size:200 },
];

export default function MainMenu({ onPlay, onNavigate, stats, onPlayOnline, onPlayBlanche, hasResumeGame = false, resumeExpired = false, onResumeMatch = null, hasNewAchievements = false }) {
  const [hovered, setHovered] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [puzzleIncomplete, setPuzzleIncomplete] = useState(false);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const completed = localStorage.getItem('checkmate_daily_puzzle_completed_date');
    setPuzzleIncomplete(completed !== today);
    const handler = () => setPuzzleIncomplete(false);
    window.addEventListener('dailyPuzzleCompleted', handler);
    return () => window.removeEventListener('dailyPuzzleCompleted', handler);
  }, []);
  const wins = stats?.wins || 0;
  const losses = stats?.losses || 0;
  const draws = stats?.draws || 0;
  const streak = stats?.currentStreak || 0;
  const hasStats = (wins + losses + draws) > 0;

  return (
    <div style={{
      minHeight: "100vh",
      background: DS.bg,
      backgroundImage: `
        repeating-conic-gradient(rgba(255,255,255,0.012) 0% 25%, transparent 0% 50%)
      `,
      backgroundSize: "64px 64px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
    }}>

      {/* Decorative background pieces */}
      {BG_PIECES.map(({ p, top, left, rot, size }) => (
        <div key={p} style={{
          position: "absolute",
          top, left,
          fontSize: size,
          opacity: 0.028,
          color: DS.text,
          transform: `rotate(${rot}deg)`,
          userSelect: "none",
          pointerEvents: "none",
          lineHeight: 1,
        }}>
          {p}
        </div>
      ))}

      {/* Content */}
      <div style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        zIndex: 1,
      }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{
            fontSize: 90,
            lineHeight: 1,
            color: DS.gold,
            marginBottom: 20,
            filter: `drop-shadow(0 0 30px ${DS.goldGlow})`,
          }}>♔</div>
          <h1 className="cinzel-deco" style={{
            margin: 0,
            fontSize: 44,
            fontWeight: 700,
            color: DS.text,
            letterSpacing: "0.12em",
          }}>
            CHECKMATE
          </h1>
          <p className="cinzel" style={{
            margin: "10px 0 0",
            fontSize: 11,
            color: DS.textMuted,
            letterSpacing: "0.4em",
            textTransform: "uppercase",
          }}>
            The Arena
          </p>
        </div>

        {/* Resume Match — shown when user has an active online game to return to */}
        {(hasResumeGame || resumeExpired) && (
          <div style={{ width: 300, marginBottom: 16 }}>
            {hasResumeGame && onResumeMatch && (
              <button
                className="cinzel"
                onClick={onResumeMatch}
                style={{
                  width: "100%", padding: "14px 20px",
                  background: "linear-gradient(135deg, rgba(115,102,240,0.22), rgba(115,102,240,0.10))",
                  border: "1px solid rgba(115,102,240,0.55)",
                  borderRadius: 10, cursor: "pointer",
                  color: "#C8B8FF", fontSize: 13, fontWeight: 700,
                  letterSpacing: "0.06em", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 10,
                  boxShadow: "0 0 18px rgba(115,102,240,0.18)",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(115,102,240,0.85)"; e.currentTarget.style.boxShadow = "0 0 24px rgba(115,102,240,0.32)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(115,102,240,0.55)"; e.currentTarget.style.boxShadow = "0 0 18px rgba(115,102,240,0.18)"; }}
              >
                <span style={{ fontSize: 16 }}>↩</span> Resume Match
              </button>
            )}
            {resumeExpired && (
              <div style={{
                textAlign: "center", padding: "10px 16px",
                background: "rgba(224,70,70,0.08)", border: "1px solid rgba(224,70,70,0.25)",
                borderRadius: 8, fontSize: 12, color: "#E04646",
              }}>
                Match expired.
              </div>
            )}
          </div>
        )}

        {/* Primary action buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 300, marginBottom: 40 }}>
          <MenuBtn
            label="♟  Play vs Bot"
            primary
            hovered={hovered === "bot"}
            onMouseEnter={() => setHovered("bot")}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onPlay("bot")}
          />
          <MenuBtn
            label="⚔  Local PvP"
            hovered={hovered === "pvp"}
            onMouseEnter={() => setHovered("pvp")}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onPlay("pvp")}
          />
          {onPlayOnline && (
            <MenuBtn
              label="⚡  Play Online"
              hovered={hovered === "online"}
              onMouseEnter={() => setHovered("online")}
              onMouseLeave={() => setHovered(null)}
              onClick={onPlayOnline}
            />
          )}
          <MenuBtn
            label="◎  Daily Puzzle"
            hovered={hovered === "puzzle"}
            onMouseEnter={() => setHovered("puzzle")}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onNavigate("puzzle")}
            alertDot={puzzleIncomplete}
          />
          <MenuBtn
            label="♛  Play vs BLANCHE"
            gold
            hovered={hovered === "blanche"}
            onMouseEnter={() => setHovered("blanche")}
            onMouseLeave={() => setHovered(null)}
            onClick={onPlayBlanche}
          />
        </div>

        {/* Divider */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16, width: isMobile ? "calc(100vw - 40px)" : 300, marginBottom: 32,
        }}>
          <div style={{ flex: 1, height: 1, background: DS.borderFaint }} />
          <span className="cinzel" style={{ fontSize: 10, color: DS.textFaint, letterSpacing: "0.15em" }}>MORE</span>
          <div style={{ flex: 1, height: 1, background: DS.borderFaint }} />
        </div>

        {/* Secondary nav — horizontal scroll on mobile */}
        <div style={{ position: "relative", marginBottom: 52, width: isMobile ? "100vw" : "auto" }}>
          <div
            className={isMobile ? "no-scrollbar" : ""}
            style={{
              display: "flex",
              gap: 8,
              ...(isMobile ? {
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                paddingLeft: 20,
                paddingRight: 44,
                paddingBottom: 4,
                flexWrap: "nowrap",
              } : {}),
            }}
          >
            {[
              { key: "trophies", label: "♔  Trophies", badge: hasNewAchievements ? "!" : null, badgeType: "gold" },
              { key: "rankings", label: "♕  Rankings" },
              { key: "shop",     label: "✦  Shop", badge: "NEW", badgeType: "new" },
              { key: "armoury",  label: "◈  Armoury" },
              { key: "stats",    label: "◆  Stats" },
              { key: "settings", label: "⚙  Settings" },
            ].map(({ key, label, badge, badgeType }) => (
              <div key={key} style={{ position: "relative", flexShrink: 0 }}>
                <button
                  className="btn btn-ghost btn-sm cinzel"
                  onClick={() => onNavigate(key)}
                  style={{
                    letterSpacing: "0.05em",
                    ...(badgeType === "gold" && badge ? {
                      borderColor: "rgba(237,187,74,0.5)",
                      boxShadow: "0 0 10px rgba(237,187,74,0.3)",
                      color: DS.gold,
                    } : {}),
                  }}
                >
                  {label}
                </button>
                {badge && badgeType === "!" && (
                  <span style={{
                    position: "absolute", top: -5, right: -5,
                    background: DS.gold, color: "#1A1200",
                    fontSize: 8, fontWeight: 700, fontFamily: "'Cinzel', serif",
                    borderRadius: "50%", width: 14, height: 14,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pointerEvents: "none",
                  }}>!</span>
                )}
                {badge && badgeType === "gold" && (
                  <span style={{
                    position: "absolute", top: -6, right: -6,
                    background: "linear-gradient(135deg, #EDBB4A, #C8950A)",
                    color: "#1A1200",
                    fontSize: 7, fontWeight: 700, fontFamily: "'Cinzel', serif",
                    borderRadius: 4, padding: "2px 4px",
                    lineHeight: 1, pointerEvents: "none",
                    boxShadow: "0 0 6px rgba(237,187,74,0.5)",
                  }}>!</span>
                )}
                {badge && badgeType === "new" && (
                  <span className="cinzel" style={{
                    position: "absolute", top: -8, right: -4,
                    background: "linear-gradient(135deg, #7366F0, #5a50d4)",
                    color: "#fff",
                    fontSize: 7, fontWeight: 700,
                    borderRadius: 4, padding: "2px 5px",
                    lineHeight: 1, pointerEvents: "none",
                    letterSpacing: "0.04em",
                    boxShadow: "0 0 6px rgba(115,102,240,0.5)",
                    whiteSpace: "nowrap",
                  }}>NEW</span>
                )}
              </div>
            ))}
          </div>
          {/* Gradient fade on right edge — mobile only */}
          {isMobile && (
            <div style={{
              position: "absolute", right: 0, top: 0, bottom: 4,
              width: 40, pointerEvents: "none",
              background: `linear-gradient(to right, transparent, ${DS.bg})`,
            }} />
          )}
        </div>

        {/* BLANCHE teaser */}
        <div style={{
          width: 300, marginBottom: 32,
          background: `linear-gradient(135deg, rgba(237,187,74,0.07), rgba(115,102,240,0.07))`,
          border: `1px solid rgba(237,187,74,0.18)`,
          borderRadius: 12, padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 14,
          cursor: "pointer",
        }}
          onClick={() => onNavigate("settings")}
        >
          <span style={{
            fontSize: 28, color: DS.gold,
            filter: `drop-shadow(0 0 8px ${DS.goldGlow})`,
            flexShrink: 0,
          }}>♛</span>
          <div>
            <div className="cinzel" style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.14em",
              color: DS.gold, marginBottom: 3,
            }}>
              BLANCHE
            </div>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.5 }}>
              Play against an AI coach that breaks down your mistakes. Stop losing the same way twice.
            </div>
          </div>
        </div>

        {/* Stats bar */}
        {hasStats ? (
          <div style={{
            background: DS.surface,
            border: `1px solid ${DS.borderFaint}`,
            borderRadius: 10,
            padding: "12px 28px",
            display: "flex",
            gap: 28,
            alignItems: "center",
          }}>
            <StatPip label="Wins"   value={wins}   color={DS.success} />
            <Divider />
            <StatPip label="Losses" value={losses} color={DS.danger}  />
            <Divider />
            <StatPip label="Draws"  value={draws}  color={DS.warning} />
            {streak > 1 && (
              <>
                <Divider />
                <StatPip label="Streak" value={`${streak} †`} color={DS.gold} />
              </>
            )}
          </div>
        ) : (
          <p className="cinzel" style={{
            fontSize: 11, color: DS.textFaint, letterSpacing: "0.15em", textTransform: "uppercase",
          }}>
            Your legend begins here
          </p>
        )}
      </div>
    </div>
  );
}

function MenuBtn({ label, primary, gold, onClick, hovered, onMouseEnter, onMouseLeave, alertDot }) {
  let bg, border, color, shadow;
  if (gold) {
    bg     = hovered ? "linear-gradient(135deg, #FFD700, #C8950A)" : "linear-gradient(135deg, rgba(237,187,74,0.18), rgba(200,149,10,0.12))";
    border = `1px solid ${hovered ? "#FFD700" : "rgba(237,187,74,0.4)"}`;
    color  = hovered ? "#1A1200" : "#EDBB4A";
    shadow = hovered ? "0 0 28px rgba(237,187,74,0.45)" : "none";
  } else if (primary) {
    bg     = hovered ? "#8478F5" : "#7366F0";
    border = `1px solid ${hovered ? "#8478F5" : "#7366F0"}`;
    color  = "#fff";
    shadow = hovered ? "0 0 28px rgba(115,102,240,0.4)" : "none";
  } else {
    bg     = hovered ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)";
    border = `1px solid ${hovered ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)"}`;
    color  = DS.text;
    shadow = "none";
  }
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="cinzel"
        style={{
          width: "100%", padding: "15px 24px",
          border, borderRadius: 10, background: bg, color,
          fontSize: 13, fontWeight: 700, letterSpacing: "0.08em",
          cursor: "pointer", transition: "all 0.15s ease",
          boxShadow: shadow,
          transform: hovered ? "translateY(-1px)" : "none",
          textTransform: "uppercase",
        }}
      >
        {label}
      </button>
      {alertDot && (
        <span style={{
          position: "absolute", top: -6, right: -6,
          background: "#E04646", color: "#fff",
          fontSize: 9, fontWeight: 700, fontFamily: "'Cinzel', serif",
          borderRadius: "50%", width: 16, height: 16,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none", boxShadow: "0 0 6px rgba(224,70,70,0.6)",
        }}>!</span>
      )}
    </div>
  );
}

function StatPip({ label, value, color }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div className="cinzel" style={{ fontSize: 18, fontWeight: 700, color: color || DS.text }}>{value}</div>
      <div className="cinzel" style={{ fontSize: 9, color: DS.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 28, background: DS.borderFaint }} />;
}
